import type { ReactNode } from 'react';
import type { EntityRefDTO, LegalForm } from '@btw/shared';
import { useT } from '../i18n';

/** Initials avatar; the colour is fixed per entity so it reads the same on every page (§8.4). */
export function EntityAvatar({ entity, size }: { entity: Pick<EntityRefDTO, 'color' | 'initials' | 'name'>; size?: 'sm' | 'lg' }) {
  return (
    <span
      className={['avatar', size && `avatar--${size}`].filter(Boolean).join(' ')}
      style={{ ['--avatar-color' as string]: entity.color }}
      aria-hidden="true"
    >
      {entity.initials}
    </span>
  );
}

/** True when the legal form is already part of the name ("ACME Holding B.V."), so we do not repeat it. */
export function nameShowsLegalForm(name: string, form: LegalForm): boolean {
  const n = name.toLowerCase().replace(/\./g, '');
  if (form === 'bv') return /\bbv\b/.test(n);
  if (form === 'vof') return /\bvof\b/.test(n);
  return false;
}

export function LegalFormLabel({ form }: { form: LegalForm }) {
  const t = useT();
  return <>{t(`legalForm.${form}`)}</>;
}

export function EntityName({ entity, sub, size }: { entity: EntityRefDTO; sub?: ReactNode; size?: 'sm' | 'lg' }) {
  const t = useT();
  return (
    <span className="entity-name">
      <EntityAvatar entity={entity} size={size} />
      <span className="entity-name__text">
        <span className="entity-name__name">{entity.name}</span>
        {sub != null ? (
          <span className="muted small">{sub}</span>
        ) : nameShowsLegalForm(entity.name, entity.legalForm) ? null : (
          <span className="muted small">{t(`legalForm.${entity.legalForm}`)}</span>
        )}
      </span>
    </span>
  );
}
